import { Fragment, useState } from "react";
import { RouteFactory } from "@app/routePaths";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import { AdUnitNames } from "@components/Advertisement/utils/types";
import Container from "@components/Container/Container";
import useArticleGiftReceived from "@hooks/useArticleGiftReceived";
import useGenerateGiftURL from "@hooks/useGenerateGiftURL";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import useIsOutbrainEnabled from "@hooks/useIsOutbrainEnabled";
import ArticleDivider from "@pages/Article/components/ArticleDivider";
import LayoutFactory from "@pages/Article/components/Layout/LayoutFactory";
import { useNextArticle } from "@pages/Article/hooks/useNextArticle";
import {
  getUrlPathFromAbsoluteUrl,
  VerticalHeaderFactory,
} from "@pages/Article/utils/helpers";
import { ArticlePageContext } from "@pages/Article/utils/types";
import { FeedTag } from "@pages/Keyword/utils/types";
import { getFormattedTextForAds } from "@pages/Section/helpers";
import { ArticleDisplayType } from "@typings/enums";

export type ArticleContentProps = Pick<ArticlePageContext, "data">;
export type ArticlePageData = ArticlePageContext["data"];

const ArticleContentAppWebView = ({
  data,
}: ArticleContentProps): React.ReactElement => {
  const [articleInView, setArticleInView] = useState<ArticlePageData>(data);
  // Article data de-construct
  const { sections, tags, paidMode, id, sentiment } = data;

  const { moreStories } = useNextArticle({ data });

  const firstArticleUrlPath =
    getUrlPathFromAbsoluteUrl(moreStories[0]?.urlPath) || "";

  const { gsChannels } = useGrapeshotData({
    urlPath: firstArticleUrlPath,
    index: -1,
    keywords: sentiment?.keywords,
  });

  const isOutbrainEnabled = useIsOutbrainEnabled();

  const mainSection = sections?.[0];
  const uniqueName = mainSection?.uniqueName || "";
  const uniqueNameAds = uniqueName?.replace("_", "/") || "";
  const verticalPath = "/" + uniqueNameAds;

  const VerticalHeaderLayout = VerticalHeaderFactory({ verticalPath });

  const handleSetArticle = (e: ArticlePageData) => {
    setArticleInView(e);
  };

  const { isGiftReceived } = useArticleGiftReceived({ paidMode });

  const disablePrestitialBasedOnUrlPath = [
    RouteFactory.paidPressRelease, // paid press release pages
    RouteFactory.section("events-awards"), // events-awards pages
  ].some((path) => data?.urlPath.startsWith(path));

  const disablePrestitialBasedOnTags = [RouteFactory.keywordsBudget2025].some(
    (keyword) => tags?.some((tag) => tag.urlPath === keyword)
  );

  const adsNames: AdUnitNames[] =
    disablePrestitialBasedOnUrlPath || disablePrestitialBasedOnTags
      ? ["lb1", "imu1", "imu2", "imu3", "midarticlespecial", "catfish"]
      : [
          "lb1",
          "imu1",
          "imu2",
          "imu3",
          "midarticlespecial",
          "prestitial",
          "catfish",
        ];

  const isPremium = paidMode === "premium";

  const {
    alacrityRes: giftURlAlacrityRes,
    isModalOpen: giftURlisModalOpen,
    setIsModalOpen: setGiftURLIsModalOpen,
    handleGenerateGiftURLModal: handleGenerateGiftURLModal,
  } = useGenerateGiftURL(isPremium);

  const articleInViewGiftRes = giftURlAlacrityRes.find(
    (res) => res.pathname === articleInView.urlPath
  );

  return (
    <div className="w-full" data-testid="article-content-component">
      <AdSettings
        adNames={adsNames}
        path={`/${uniqueNameAds}`}
        pageType="articles"
        adTargets={[{ key: "page", value: "article" }]}
        slotTargets={[
          { key: "btarticleid", value: id },
          {
            key: "bttags",
            value:
              data?.tags
                ?.map((tag: FeedTag) => getFormattedTextForAds(tag.name))
                .join(",") || "",
          },
          { key: "gs_channels", value: gsChannels },
        ]}
      />
      <>
        {VerticalHeaderLayout ? (
          <Container rootClassName="py-2">
            <VerticalHeaderLayout />
          </Container>
        ) : null}

        <div id="article-content-wrapper" className="w-full">
          {moreStories.map((article, index) => {
            // Check if article is undefined
            if (!article) return null;

            const displayType =
              article?.displayType || ArticleDisplayType.MainNewsDefault;

            const ArticleLayout = LayoutFactory({
              displayType,
              path: article.urlPath,
            });

            if (!ArticleLayout) return null;

            return (
              <Fragment key={article.id}>
                {index > 0 ? <ArticleDivider /> : null}

                <ArticleLayout
                  article={article}
                  index={index}
                  gsChannels={gsChannels}
                  handleGenerateGiftURLModal={handleGenerateGiftURLModal}
                  setIsModalOpen={setGiftURLIsModalOpen}
                  alacrityRes={articleInViewGiftRes?.alacrityRes}
                  isPremium={isPremium}
                  isModalOpen={giftURlisModalOpen}
                  setArticleObj={handleSetArticle}
                  articleInView={articleInView}
                  isGifted={isGiftReceived}
                  isPreview={data.isPreview}
                  isOutbrainEnabled={isOutbrainEnabled}
                  readMoreSectionOrTagData={article.readMoreSectionOrTagData}
                />
              </Fragment>
            );
          })}
        </div>

        <Ad adType="catfish" />
        <Ad adType="prestitial" />
      </>
    </div>
  );
};

export default ArticleContentAppWebView;
